import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Turnstile from "react-turnstile";
import { authService, CAPTCHA, USER_TYPE } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Button, Input, Select } from "../../components/ui";
import Icon from "../../components/ui/Icon";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setProfile, toast } = useApp();
  const { t, tx } = useI18n();

  const [form, setForm] = useState({ email: "", password: "", remember: true });
  const [loginAs, setLoginAs] = useState(USER_TYPE.admin);
  // อีเมลเดียวสมัครได้หลายร้าน — ถ้าเจอหลายบัญชีต้องให้เลือกร้านก่อน
  const [shopOptions, setShopOptions] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaKey, setCaptchaKey] = useState(Date.now());
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const resetShopChoice = () => {
    setShopOptions([]);
    setOrgId("");
  };

  const onChange = (key) => (event) => {
    if (key === "email") resetShopChoice();
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!form.email || !form.password) {
      setError(tx("กรุณากรอกอีเมลและรหัสผ่าน", "Please fill in email and password"));
      return;
    }

    if (shopOptions.length && !orgId) {
      setError(tx("กรุณาเลือกร้านที่ต้องการเข้าใช้งาน", "Please select a shop"));
      return;
    }

    setLoading(true);
    try {
      const profile = await authService.login({ ...form, captchaToken, loginAs, orgId });
      setProfile(profile);
      toast(tx("เข้าสู่ระบบสำเร็จ", "Logged in"), "success");
      // ยึด user_type ที่ได้จริง (ชีต users เป็นตัวตัดสิน) ไม่ใช่ปุ่มที่ผู้ใช้กด
      const target = location.state?.from || (profile.user_type === USER_TYPE.admin ? "/admin" : "/app");
      navigate(target, { replace: true });
    } catch (err) {
      if (err.accounts) {
        // ยังไม่ได้ยิง Backend — ให้เลือกร้านแล้วกดเข้าสู่ระบบอีกครั้ง
        setShopOptions(err.accounts);
        setOrgId(err.accounts[0]?.org_id || "");
      }
      setError(err.message);
      setCaptchaToken("");
      setCaptchaKey(Date.now());
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 6 }}>{t("auth.loginTitle")}</h1>
      <p className="u-muted u-mb-16">{tx("เข้าใช้งานระบบบริหารธุรกิจของคุณ", "Sign in to your business workspace")}</p>

      <div className="u-fs-13 u-strong u-mb-8">{t("auth.loginAs")}</div>
      <div className="auth-role">
        <button type="button" className={loginAs === USER_TYPE.admin ? "is-active" : ""} onClick={() => { resetShopChoice(); setLoginAs(USER_TYPE.admin); }}>
          <Icon name="desktop" /> {t("auth.admin")}
        </button>
        <button type="button" className={loginAs === USER_TYPE.customer ? "is-active" : ""} onClick={() => { resetShopChoice(); setLoginAs(USER_TYPE.customer); }}>
          <Icon name="mobile" /> {t("auth.customer")}
        </button>
      </div>

      <form onSubmit={submit}>
        <Input
          label={t("common.email")}
          type="email"
          placeholder="you@company.com"
          value={form.email}
          onChange={onChange("email")}
          autoComplete="username"
        />
        <Input
          label={t("auth.password")}
          type="password"
          placeholder="••••••••"
          value={form.password}
          onChange={onChange("password")}
          autoComplete="current-password"
        />

        {shopOptions.length ? (
          <Select
            label={tx("เลือกร้าน", "Select shop")}
            options={shopOptions.map((row) => ({ value: row.org_id, label: row.org_name || row.org_id }))}
            value={orgId}
            onChange={(event) => setOrgId(event.target.value)}
          />
        ) : null}

        {CAPTCHA.siteKey ? (
          <div className="u-mb-16">
            <Turnstile
              key={captchaKey}
              sitekey={CAPTCHA.siteKey}
              onVerify={setCaptchaToken}
              onExpire={() => setCaptchaToken("")}
              onError={() => setCaptchaToken("")}
            />
          </div>
        ) : null}

        {error ? <div className="field-error u-mb-8">{error}</div> : null}

        <Button type="submit" variant="primary" block disabled={loading}>
          {loading ? tx("กำลังเข้าสู่ระบบ...", "Signing in...") : t("action.login")}
        </Button>
      </form>

      <div className="u-between u-mt-16 u-fs-13">
        <Link to="/auth/register">{tx("เปิดร้านใหม่ (ผู้ดูแล)", "Open a shop (admin)")}</Link>
        <Link to="/auth/forgot">{t("auth.forgot")}</Link>
      </div>

      <div className="u-fs-13 u-muted u-mt-8">
        {tx(
          "ลูกค้าสมัครสมาชิกผ่านลิงก์ที่ร้านส่งให้เท่านั้น",
          "Customers sign up through the link their shop provides."
        )}
      </div>
    </>
  );
}
